import React, { cloneElement, useEffect, useRef } from "react";

import { ReactComponent as DisconnectIcon } from "../../assets/img/navbar/disconnect-icon.svg";
import { ReactComponent as NotificationIcon } from "../../assets/img/navbar/notification-icon.svg";
import { ReactComponent as ProfileIcon } from "../../assets/img/navbar/profile-icon.svg";
import { ReactComponent as WalletIcon } from "../../assets/img/navbar/disconnect-icon.svg";
import { NavLink, useLocation } from "react-router-dom";
import { FaUserEdit, FaWallet } from "react-icons/fa";
import { IoIosSwitch } from "react-icons/io";
import { HiOutlineLogout } from "react-icons/hi";

import { useDisconnect, useChainId, useChains, useAccount } from "wagmi";
import { useConnectModal, useChainModal } from "@rainbow-me/rainbowkit";
import useHandleCreateProject from "../../hooks/useHandleCreateProject";
import { Oval } from "react-loader-spinner";
import { accessToken, role } from "../../utils/localData";
import {
  selectGeneral,
  setRegisterModalType,
  setShowAccountPopup,
  setShowNetworkPopup,
  setShowRegisterModal,
  setShowAuthModal,
} from "../../redux/slices/generalSlice";
import { useDispatch, useSelector } from "react-redux";
import { UN_REGISTERED } from "../../Constants";
import NewNotifIcon from "../common/NewNotifIcon";
import useOutsideClick from "../../hooks/useOutsideClick";
import useAuth from "../../hooks/useAuth";
import { MdClose } from "react-icons/md";
import { currencyIcons } from "../../utils/currencies";
import { scroller } from "react-scroll";
import { BsGridFill } from "react-icons/bs";

const AccountItem = ({ data, isButton = false }) => {
  const { icon, title, link, itemClass, onClick = () => {}, badge } = data;
  const location = useLocation();

  return (
    <>
      {isButton ? (
        <button
          onClick={onClick}
          className={`w-full font-semibold py-3.5 px-3 flex items-center rounded-xl bg-whitesmoke hover:bg-custom-gray-400 ${itemClass}`}
        >
          <span className="relative">
            {badge && cloneElement(badge)}
            {cloneElement(icon)}
          </span>
          <span className="">{title}</span>
        </button>
      ) : (
        <NavLink
          onClick={onClick}
          to={link}
          className={({ isActive }) =>
            `w-full font-semibold py-3.5 px-3 flex items-center rounded-xl relative ${
              isActive &&
              link.split("?")[0] === location.pathname + location.hash
                ? "border border-primary bg-purple-200"
                : "bg-whitesmoke hover:bg-custom-gray-400"
            } `
          }
        >
          <span className="relative">
            {badge && cloneElement(badge)}
            {cloneElement(icon)}
          </span>

          <span className="text-black">{title}</span>
        </NavLink>
      )}
    </>
  );
};
const AccountPopup = () => {
  // Get current chain using Wagmi v2 hooks
  const chainId = useChainId();
  const chains = useChains();
  const chain = chains?.find((c) => c.id === chainId);
  
  // RainbowKit hooks
  const { openConnectModal } = useConnectModal();
  const { openChainModal } = useChainModal();

  const { showAccountPopup: show } = useSelector(selectGeneral);

  // Get wallet connection status
  const { isConnected } = useAccount();

  // Close modal handler using Redux dispatch
  const handleClose = () => {
    dispatch(setShowAccountPopup(false));
  };

  // Ref for modal element
  const modalRef = useRef(null);
  // Handle outside click to close the modal
  useOutsideClick(modalRef, handleClose, null, show);

  // Redux dispatch and custom hooks for authentication
  const dispatch = useDispatch();
  const { logout, authLoading } = useAuth();

  // Close popup handler using Redux dispatch
  const handleClosePopUp = () => {
    dispatch(setShowAccountPopup(false));
  };

  // Custom hook for disconnecting from the wallet
  const { disconnect } = useDisconnect();
  // Handle disconnecting wallet only (keep social auth logged in)
  const handleDisconnectWallet = () => {
    disconnect();
    handleClosePopUp();
  };
  
  // Handle full logout (for social auth users)
  // Uses useAuth().logout which properly disconnects wallet AND clears auth state
  const handleLogout = () => {
    handleClosePopUp();
    logout();
  };

  // Handle connecting wallet for social auth users via RainbowKit
  const handleConnectWallet = () => {
    if (openConnectModal) {
      openConnectModal();
    }
    handleClosePopUp();
  };

  // Custom hook for handling project creation
  const { handleCreateProject } = useHandleCreateProject();
  // Handle project creation and closing the popup
  const CreateProject = () => {
    handleCreateProject();
    handleClosePopUp();
  };

  // Handle checking the user's profile - open AuthModal for proper auth flow
  const handleCheckProfile = () => {
    // If no token, show the proper auth flow (email/Google signup)
    if (!accessToken()) {
      dispatch(setShowAuthModal({ show: true }));
    } else {
      // User has token but incomplete profile - show register modal
      dispatch(setRegisterModalType("withoutSignupBtn"));
      dispatch(setShowRegisterModal(true));
    }
    handleClosePopUp();
  };

  // Effect to manage body overflow based on popup visibility
  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [show]);

  // Handle showing the network popup via RainbowKit
  const handleShowNetworkPopup = () => {
    if (openChainModal) {
      openChainModal();
    }
    handleClosePopUp();
  };

  // Data for account items with titles, icons, links, and click handlers
  const accountItems = [
    {
      title: "View Profile",
      icon: <ProfileIcon className="mr-1.5 h-[24px] cursor-pointer" />,
      link: "/profile",
      onClick: handleClosePopUp,
    },
    {
      title: "My Projects",
      icon: (
        <BsGridFill
          color={"#3c2166"}
          size={22}
          className="mr-1.5 cursor-pointer"
        />
      ),
      link: "/profile#projects",
      onClick: () => {
        scroller.scrollTo("projects", {
          duration: 1000,
          smooth: "easeInOutQuint",
          offset: -130,
        });
        handleClosePopUp();
      },
    },
    {
      title: "Notifications",
      icon: <NotificationIcon className="mr-1.5 h-6 cursor-pointer" />,
      link: "/notifications",
      onClick: handleClosePopUp,
      badge: <NewNotifIcon className="absolute left-0 top-0 w-2.5 h-2.5" />,
    },
  ];

  return (
    <div className="min-w-[100vw] w-full h-screen flex justify-center items-center bg-[#241b4795] backdrop-blur-md fixed top-0 left-0  roboto-font z-50 ">
      <div
        ref={modalRef}
        className="w-11/12 md:w-[450px] flex flex-col items-center pb-5 pt-1 rounded-3xl bg-white "
      >
        <div className="w-full flex justify-end py-3 px-5">
          <button onClick={handleClose}>
            <MdClose className="" size={26} />
          </button>
        </div>

        {/* /////////////// */}

        {authLoading ? (
          <div className="w-full h-full flex justify-center items-center">
            <Oval
              height={30}
              width={30}
              color="#8538FF"
              wrapperStyle={{}}
              wrapperClass=""
              visible={true}
              ariaLabel="oval-loading"
              secondaryColor="#8538FF"
              strokeWidth={2}
              strokeWidthSecondary={2}
            />{" "}
          </div>
        ) : (
          <div className="w-full flex flex-col px-6 gap-2">
            <div className="flex flex-col ">
              {accessToken() && role() !== UN_REGISTERED ? (
                <div className="flex flex-col gap-y-2 ">
                  {accountItems.map((item, index) => (
                    <AccountItem key={index} data={item} />
                  ))}
                  
                </div>
              ) : (
                <AccountItem
                  data={{
                    title: "Create Profile",
                    icon: <ProfileIcon className="mr-1.5 h-6 cursor-pointer" />,
                    onClick: handleCheckProfile,
                  }}
                  isButton
                />
              )}
            </div>
            
            {/* Wallet Connection Section */}
            {isConnected ? (
              <>
                {/* Show wallet disconnect option when connected */}
                <AccountItem
                  data={{
                    title: "Disconnect Wallet",
                    icon: <DisconnectIcon className="mr-1.5 h-6 cursor-pointer" />,
                    onClick: handleDisconnectWallet,
                    itemClass: "text-[#E93C3C]",
                  }}
                  isButton
                />
              </>
            ) : (
              <>
                {/* Show connect wallet option when not connected */}
                <AccountItem
                  data={{
                    title: "Connect Wallet",
                    icon: <FaWallet className="mr-1.5 h-5 cursor-pointer text-primary" />,
                    onClick: handleConnectWallet,
                    itemClass: "text-primary border border-primary/30 bg-primary/5",
                  }}
                  isButton
                />
              </>
            )}
            
            {/* Logout - only show when user is logged in */}
            {accessToken() && (
              <AccountItem
                data={{
                  title: "Log Out",
                  icon: <HiOutlineLogout className="mr-1.5 h-6 cursor-pointer" />,
                  onClick: handleLogout,
                  itemClass: "text-gray-600",
                }}
                isButton
              />
            )}
            
            <button
              onClick={CreateProject}
              className="bg-light-primary py-2.5 w-full text-center text-white text-sm  rounded-[24px] my-5"
            >
              Create Project
            </button>
          </div>
        )}
        {/* /////////////// */}
      </div>
    </div>
  );
};

export default AccountPopup;
